import { useState, useEffect, useCallback } from 'react';
import socialApi from '../services/socialApi';

export const useSocial = () => {
  const [activityFeed, setActivityFeed] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [feedLoading, setFeedLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMoreActivities, setHasMoreActivities] = useState(true);

  const fetchActivityFeed = useCallback(async (limit = 20, offset = 0, refresh = false) => {
    if (refresh) {
      setFeedLoading(true);
    } else {
      setLoading(true);
    }
    
    setError(null);
    
    try {
      const response = await socialApi.getActivityFeed(limit, offset);
      const newActivities = response.activities || [];
      
      if (refresh || offset === 0) {
        setActivityFeed(newActivities);
      } else {
        setActivityFeed(prev => [...prev, ...newActivities]);
      }
      
      setHasMoreActivities(response.hasMore !== false);
    } catch (error) {
      console.error('Error fetching activity feed:', error);
      setError(error.message || 'Failed to load activity feed');
    } finally {
      setLoading(false);
      setFeedLoading(false);
    }
  }, []);

  const fetchLeaderboard = useCallback(async (period = 'weekly') => {
    try {
      const response = await socialApi.getFriendLeaderboard(period);
      setLeaderboard(response.leaderboard || []);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    }
  }, []);

  const fetchUserProfile = useCallback(async (userId) => {
    try {
      const response = await socialApi.getUserProfile(userId);
      setUserProfile(response.user || null);
      return response.user;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw error;
    }
  }, []);

  const shareActivity = useCallback(async (activityData) => {
    try {
      const response = await socialApi.shareActivity(activityData);
      
      // Add the new activity to the top of the feed
      if (response.success) {
        const newActivity = {
          id: response.activityId,
          user: { name: 'You' }, // This would be the current user
          ...activityData,
          timestamp: new Date().toISOString(),
          likes: 0,
          comments: 0,
          isLiked: false
        };
        
        setActivityFeed(prev => [newActivity, ...prev]);
      }
      
      return response;
    } catch (error) {
      console.error('Error sharing activity:', error);
      throw error;
    }
  }, []);

  const toggleLike = useCallback(async (activityId, shouldLike) => {
    try {
      // Optimistically update the UI
      setActivityFeed(prev => prev.map(activity => {
        if (activity.id === activityId) {
          return {
            ...activity,
            isLiked: shouldLike,
            likes: shouldLike ? activity.likes + 1 : Math.max(0, activity.likes - 1)
          };
        }
        return activity;
      }));

      // Make the API call
      const response = shouldLike 
        ? await socialApi.likeActivity(activityId)
        : await socialApi.unlikeActivity(activityId);
      
      return response;
    } catch (error) {
      console.error('Error toggling like:', error);
      
      // Revert the optimistic update on error
      setActivityFeed(prev => prev.map(activity => {
        if (activity.id === activityId) {
          return {
            ...activity,
            isLiked: !shouldLike,
            likes: shouldLike ? Math.max(0, activity.likes - 1) : activity.likes + 1
          };
        }
        return activity;
      }));
      
      throw error;
    }
  }, []);

  const addComment = useCallback(async (activityId, comment) => {
    try {
      const response = await socialApi.commentOnActivity(activityId, comment);
      
      // Update the activity feed to reflect the new comment
      if (response.success) {
        setActivityFeed(prev => prev.map(activity => {
          if (activity.id === activityId) {
            return {
              ...activity,
              comments: activity.comments + 1
            };
          }
          return activity;
        }));
      }
      
      return response;
    } catch (error) {
      console.error('Error adding comment:', error);
      throw error;
    }
  }, []);

  const getActivityComments = useCallback(async (activityId) => {
    try {
      const response = await socialApi.getActivityComments(activityId);
      return response.comments || [];
    } catch (error) {
      console.error('Error fetching comments:', error);
      return [];
    }
  }, []);

  const getUserActivities = useCallback(async (userId, limit = 10) => {
    try {
      const response = await socialApi.getUserActivities(userId, limit);
      return response.activities || [];
    } catch (error) {
      console.error('Error fetching user activities:', error);
      return [];
    }
  }, []);

  const refreshFeed = useCallback(() => {
    fetchActivityFeed(20, 0, true);
  }, [fetchActivityFeed]);

  const loadMoreActivities = useCallback(() => {
    if (hasMoreActivities && !loading) {
      fetchActivityFeed(20, activityFeed.length);
    }
  }, [fetchActivityFeed, hasMoreActivities, loading, activityFeed.length]);

  // Load initial data
  useEffect(() => {
    fetchActivityFeed();
    fetchLeaderboard();
  }, [fetchActivityFeed, fetchLeaderboard]);

  return {
    activityFeed,
    leaderboard,
    userProfile,
    loading,
    feedLoading,
    error,
    hasMoreActivities,
    fetchActivityFeed,
    fetchLeaderboard,
    fetchUserProfile,
    shareActivity,
    toggleLike,
    addComment,
    getActivityComments,
    getUserActivities,
    refreshFeed,
    loadMoreActivities,
    setError
  };
};